import { useState } from "react";
import { Smartphone, Terminal as TerminalIcon, Monitor, KeyRound, Wifi } from "lucide-react";

const CATEGORY_ICON = {
  countertop: TerminalIcon,
  wireless: Wifi,
  smart: Monitor,
  pinpad: KeyRound,
  mobile: Smartphone,
};

/**
 * Renders the product image for a terminal.
 * Looks for /terminals/{slug}.{png|jpg|webp} in /public.
 * Falls back to a branded SVG-style placeholder showing the category icon + model name.
 *
 * To use real product photos:
 *   1. Drop image into /app/frontend/public/terminals/{slug}.png
 *   2. The card will switch to the real image automatically.
 */
export default function TerminalImage({ terminal, className = "" }) {
  const [errored, setErrored] = useState(false);
  const Icon = CATEGORY_ICON[terminal.category] || TerminalIcon;
  const src = `/terminals/${terminal.slug}.png`;

  if (errored) {
    return (
      <div
        className={`relative w-full h-full overflow-hidden ${className}`}
        style={{
          background:
            "radial-gradient(ellipse 80% 80% at 50% 30%, rgba(0,114,255,0.18) 0%, rgba(234,243,255,0.6) 50%, #EEF2F6 100%)",
        }}
        aria-label={`${terminal.brand} ${terminal.name}`}
      >
        {/* Dot pattern */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(0,114,255,0.18) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        />
        {/* Subtle accent glow */}
        <div
          className="absolute -top-16 -right-16 w-56 h-56 rounded-full"
          style={{ background: "rgba(0,114,255,0.18)", filter: "blur(60px)" }}
        />

        <div className="relative h-full w-full flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white border border-[var(--swipe-accent)]/15 shadow-[0_18px_40px_-18px_rgba(6,26,58,0.25)] flex items-center justify-center mb-5">
            <Icon className="w-10 h-10 md:w-12 md:h-12 text-[var(--swipe-accent)]" strokeWidth={1.7} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--swipe-accent)]">
            {terminal.brand}
          </div>
          <div className="mt-1.5 font-display-tight text-[18px] md:text-[20px] font-extrabold text-[var(--swipe-navy)] leading-tight">
            {terminal.name}
          </div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={`${terminal.brand} ${terminal.name}`}
      onError={() => setErrored(true)}
      className={`w-full h-full object-contain ${className}`}
      style={{ background: "linear-gradient(180deg, #EAF3FF 0%, #EEF2F6 100%)" }}
    />
  );
}
